'use client';

import { c as _c } from "react/compiler-runtime";
import { type inferParserType, parseAsArrayOf, parseAsInteger, parseAsString, parseAsStringEnum, useQueryState, useQueryStates } from 'nuqs';
import { z } from 'zod';
import { generalConfig } from '@sola/config/lib/getters.ts';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import { ArticleTabsEnum } from '../molecules/article-group/article-term-list.tsx';
const limitValues = ['4', '8', '12', '24'] as const;
export const ArticleLimitEnum = z.enum(limitValues);
export type ArticleLimitEnum = z.infer<typeof ArticleLimitEnum>;
const giftCatalogSortValues = ['mostNeeded', 'priceLowToHigh', 'priceHighToLow', 'alphabeticalAZ', 'alphabeticalZA'] as const;
export const GiftCatalogSortEnum = z.enum(giftCatalogSortValues);
export type GiftCatalogSortEnum = z.infer<typeof GiftCatalogSortEnum>;
const sortByValues = ['promotedContent', 'desc', 'asc'] as const;
export const ArticleSortByEnum = z.enum(sortByValues);
export type ArticleSortByEnum = z.infer<typeof ArticleSortByEnum>;
export const usePaginationQueryParams = t0 => {
  const $ = _c(7);
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      history: t4
    } = t0 === undefined ? {} : t0;
    const history = t4 === undefined ? "push" : t4;
    t1 = useQueryState;
    t2 = "page";
    t3 = parseAsInteger.withDefault(0).withOptions({
      history
    });
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  const [currentPage, setCurrentPage] = t1(t2, t3);
  let t4;
  if ($[4] !== currentPage || $[5] !== setCurrentPage) {
    t4 = {
      currentPage,
      setCurrentPage
    };
    $[4] = currentPage;
    $[5] = setCurrentPage;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
export const useSearchQueryParams = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsString.withDefault("");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [searchFilters, setSearchFilters] = useQueryState("q", t0);
  let t1;
  if ($[1] !== searchFilters || $[2] !== setSearchFilters) {
    t1 = {
      searchFilters,
      setSearchFilters
    };
    $[1] = searchFilters;
    $[2] = setSearchFilters;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const giftCatalogParamsSchema = {
  category: parseAsArrayOf(parseAsString, ',').withDefault([]),
  promotion: parseAsString.withDefault(''),
  priceMin: parseAsInteger.withDefault(generalConfig.catalogFilterMin),
  priceMax: parseAsInteger.withDefault(generalConfig.catalogFilterMax),
  sortby: parseAsString.withDefault(GiftCatalogSortEnum.enum.mostNeeded)
};
export const useGiftCatalogQueryParams = () => {
  const $ = _c(3);
  const [giftCatalogFilters, setGiftCatalogFilters] = useQueryStates(giftCatalogParamsSchema);
  let t0;
  if ($[0] !== giftCatalogFilters || $[1] !== setGiftCatalogFilters) {
    t0 = {
      giftCatalogFilters,
      setGiftCatalogFilters
    };
    $[0] = giftCatalogFilters;
    $[1] = setGiftCatalogFilters;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const useTabGroupQueryParams = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsString.withDefault(ArticleTabsEnum.enum.all);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [tabGroupFilters, setTabGroupFilters] = useQueryState("tab", t0);
  let t1;
  if ($[1] !== setTabGroupFilters || $[2] !== tabGroupFilters) {
    t1 = {
      tabGroupFilters,
      setTabGroupFilters
    };
    $[1] = setTabGroupFilters;
    $[2] = tabGroupFilters;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export type ArticleQueryParams = {
  limit?: number;
  sortby?: ArticleSortByEnum;
  topic?: string;
  theme?: string;
  country?: string;
};
export const useArticleQueryParams = props => {
  const $ = _c(10);
  let t0;
  if ($[0] !== props) {
    t0 = props || {};
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    perPage
  } = t0;
  let t1;
  if ($[2] !== perPage) {
    t1 = parseAsInteger.withDefault(perPage || 12);
    $[2] = perPage;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = parseAsString.withDefault(ArticleSortByEnum.enum.desc);
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1) {
    t3 = {
      limit: t1,
      sortby: t2,
      topic: parseAsString,
      theme: parseAsString,
      country: parseAsString
    };
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const articleQueryParamsSchema = t3;
  const [articleFilters, setArticleFilters] = useQueryStates(articleQueryParamsSchema);
  let t4;
  if ($[7] !== articleFilters || $[8] !== setArticleFilters) {
    t4 = {
      articleFilters,
      setArticleFilters
    };
    $[7] = articleFilters;
    $[8] = setArticleFilters;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export const fundQueryParamsSchema = {
  oneTime: parseAsArrayOf(parseAsInteger),
  monthly: parseAsArrayOf(parseAsInteger),
  annually: parseAsArrayOf(parseAsInteger),
  defaultFrequency: parseAsStringEnum(Object.values(CommitmentFrequencyEnum) as CommitmentFrequencyEnum[]),
  defaultOneTime: parseAsInteger,
  defaultMonthly: parseAsInteger,
  defaultAnnually: parseAsInteger
};
export type FundQueryParams = inferParserType<typeof fundQueryParamsSchema>;
export const useFundQueryParams = () => {
  const $ = _c(3);
  const [fundParams, setFundParams] = useQueryStates(fundQueryParamsSchema);
  let t0;
  if ($[0] !== fundParams || $[1] !== setFundParams) {
    t0 = {
      fundParams,
      setFundParams
    };
    $[0] = fundParams;
    $[1] = setFundParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};